import Pagination, { SearchResultStats } from 'components/PaginationControls'
import PositionListItem from 'components/PositionListItem'
import { Spinner, SpinnerWithText } from 'components/Spinner'
import Toggle from 'components/Toggle'
import { POSITIONS_PER_PAGE } from 'hooks/useV3Positions'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { StyledInternalLink, ThemedText } from 'theme'
import { PositionDetails } from 'types/position'

import HeaderRow from './HeaderRow'
import * as S from './styled'

type PositionListProps = React.PropsWithChildren<{
  positions: PositionDetails[]
  openPositions: PositionDetails[]
  closedPositions: PositionDetails[]
  setUserHideClosedPositions: any
  userHideClosedPositions: boolean
  loading?: boolean
  totalCount?: number
  currentPage?: number
  totalPages?: number
  onPageChange?: (page: number) => void
  searchParam?: string | null
  isSearching?: boolean
  transitionLabel?: string
  showTransitionOverlay?: boolean
}>

const DataTransitionOverlay = styled.div`
  position: absolute;
  top: 58px;
  left: 0;
  right: 0;
  bottom: 0;

  background: ${({ theme }) => `${theme.cardPrimary}`};

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 200;

  border-radius: 16px;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);

  animation: fadeIn 200ms ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

export default function PositionList({
  positions,
  setUserHideClosedPositions,
  userHideClosedPositions,
  loading = false,
  totalCount = 0,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  searchParam,
  isSearching = false,
  transitionLabel, // Add this prop
  showTransitionOverlay = false, // Add this prop
}: PositionListProps) {
  const handleToggleClosedPositions = useCallback(() => {
    setUserHideClosedPositions(!userHideClosedPositions)
  }, [setUserHideClosedPositions, userHideClosedPositions])

  const positionItems = useMemo(() => {
    return positions.map((position, index) => (
      <PositionListItem key={`${position.tokenId}-${index}`} positionDetails={position} row={index} />
    ))
  }, [positions])

  const displayStats = useMemo(() => {
    const startIndex = totalCount > 0 ? (currentPage - 1) * POSITIONS_PER_PAGE + 1 : 0
    const endIndex = Math.min(currentPage * POSITIONS_PER_PAGE, totalCount)
    return { startIndex, endIndex }
  }, [currentPage, totalCount])

  const getTransitionLabel = () => {
    if (transitionLabel) {
      return transitionLabel
    }

    if (searchParam) {
      const isIdLike = /^\d+$/.test(searchParam.trim())
      if (isIdLike) {
        return `Searching for position "${searchParam}"...`
      }
      return `Searching positions for "${searchParam}"...`
    }

    if (loading) {
      return 'Loading positions...'
    }

    return 'Searching positions...'
  }

  const shouldShowOverlay =
    showTransitionOverlay || (isSearching && !showTransitionOverlay) || (loading && !showTransitionOverlay)

  return (
    <S.MainContentWrapper>
      <S.TableWrapper>
        {shouldShowOverlay && (
          <DataTransitionOverlay>
            <SpinnerWithText>
              <Spinner />
              <ThemedText.DeprecatedBody color="white">{getTransitionLabel()}</ThemedText.DeprecatedBody>
            </SpinnerWithText>
          </DataTransitionOverlay>
        )}

        <S.Table>
          <HeaderRow />

          {shouldShowOverlay ? (
            <tbody>
              <tr>
                <td colSpan={7} style={{ height: '400px', width: '100%' }}></td>
              </tr>
            </tbody>
          ) : (
            <tbody>{positionItems}</tbody>
          )}
        </S.Table>

        {!shouldShowOverlay && totalPages > 1 && onPageChange && (
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
        )}

        {searchParam && !shouldShowOverlay && (
          <SearchResultStats>
            Showing {displayStats.startIndex}-{displayStats.endIndex} of {totalCount} position
            {totalCount !== 1 ? 's' : ''}
            {searchParam && ` matching "${searchParam}"`}
          </SearchResultStats>
        )}
      </S.TableWrapper>

      {!shouldShowOverlay && (
        <S.PositionListFooter>
          <S.ToggleWrap>
            <S.ToggleLabel>
              <>Hide closed positions</>
            </S.ToggleLabel>
            <Toggle
              id="desktop-hide-closed-positions"
              isActive={userHideClosedPositions}
              useAlternateBackground={true}
              toggle={handleToggleClosedPositions}
            />
          </S.ToggleWrap>
          <S.ResponsiveButtonPrimary
            data-cy="join-pool-button"
            id="join-pool-button"
            as={StyledInternalLink}
            to="/liquidity/v3/add"
          >
            New Position
          </S.ResponsiveButtonPrimary>
        </S.PositionListFooter>
      )}
    </S.MainContentWrapper>
  )
}
